/**
 * The one way a mutation's `onError` reports its failure (#844).
 *
 * `routeMutationError` (the app QueryClient's MutationCache `onError`, wired in
 * main.tsx) already shows every centrally routed failure (`isGloballyRouted`:
 * 5xx and transport) ONCE: in the band of the dialog the mutation's hook was
 * called in while that dialog is open, else on the global banner (#1174,
 * `meta.feedbackLayer`). A component that pushed
 * its own copy for the same failure showed it twice. This helper pushes the
 * component's copy only for the failures that are the component's own (4xx,
 * validation, non-HTTP throws) and leaves the rest to the central routing.
 *
 * `copy`:
 *   - a string is the fallback for `extractApiMessage(error, copy)` — the
 *     server's `detail` when it sends one, else this copy;
 *   - `{ message }` is pushed verbatim: the site chose its text itself (a fixed
 *     copy, `saveErrorMessage(...)`, a refusal's own sentence).
 *   - `{ message, severity: 'warning' }` (#1163): a refusal the site handles as
 *     an outcome rather than a failure (the merge send-code budget: the code
 *     step stays open) is shown as a warning. Default `'error'`.
 *
 * Guard: `src/api/__tests__/mutationErrorOnce.guard.test.ts` — every
 * production `useMutation` onError that reports feedback calls this.
 */
import type { FeedbackZoneContextValue } from '@/components/ui/FeedbackZone';
import { extractApiMessage } from '@/lib/apiError';
import { classifyFailure, isGloballyRouted } from '@/api/failureClass';
import { currentFeedbackSource } from '@/api/feedbackSourceScope';

/** The severities a mutation's own copy is shown at. */
export type MutationErrorSeverity = 'error' | 'warning';

/** A feedback zone, or a `showAlert(message, severity)`-shaped function. */
export type MutationErrorSink =
  | Pick<FeedbackZoneContextValue, 'push'>
  | ((message: string, severity: MutationErrorSeverity, source?: string) => unknown);

export type MutationErrorCopy = string | { message: string; severity?: MutationErrorSeverity };

/**
 * Push `copy` for `error` to `sink` unless the central routing shows it.
 * Returns true when it pushed.
 */
export function reportMutationError(
  sink: MutationErrorSink | null | undefined,
  error: unknown,
  copy: MutationErrorCopy,
  /**
   * The action's key (#1628): a later success of the same action clears this
   * push. Default: the key of the mutation whose callback is running
   * (`feedbackSourceScope.ts`), except for a cancel-class end, which is pushed
   * unsourced so it never replaces the earlier failure.
   */
  explicitSource?: string,
): boolean {
  if (isGloballyRouted(error) || !sink) return false;
  const source =
    explicitSource ?? (classifyFailure(error) === 'cancel' ? undefined : currentFeedbackSource());
  const message = typeof copy === 'string' ? extractApiMessage(error, copy) : copy.message;
  const severity = typeof copy === 'string' ? 'error' : (copy.severity ?? 'error');
  if (typeof sink === 'function') {
    if (source === undefined) sink(message, severity);
    else sink(message, severity, source);
  } else if (source === undefined) sink.push(message, severity);
  else sink.push(message, severity, { source });
  return true;
}
